import { setLayoutMode } from "../LayoutSlice_Store";
import { selectCurrentProjectType } from "../ProjectSlice_Store";
import {
  addGenerationProgress,
  setChatHistoryError,
  setChatHistoryLoading,
  setMessages,
  setSelectedUseCase,
  setTurnTodoPlan,
  setUseCaseSelected,
  updateGenerationStatus,
} from "./chatSlice";
import { createConversationLoadAbortController } from "./chatModuleState";
import { buildRehydratedHistory } from "./rehydrateMessages";
import { restoreHistoricalOutputs, restoreWorkspacePayload } from "./rehydrateWorkspace";
import type { ChatDispatch } from "./chatSliceTypes";

const TEMP_PROJECT_PREFIX = "temp-";
const OFFER_LETTER_USE_CASE = "offer-letter";
const IN_PROGRESS_STATUSES = ["generating", "processing"];

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === "AbortError";

/** Rebuild the transcript, the use case and every restored todo plan. */
function applyRehydratedHistory(history: any, dispatch: ChatDispatch, getState: () => any): void {
  const { messages, todoPlans } = buildRehydratedHistory(history.turns);
  console.log("🔄 [REHYDRATE] Rehydrated messages:", messages.length);
  console.log("🔄 [REHYDRATE] Rehydrated todo plans:", Object.keys(todoPlans).length);

  // setMessages replaces the array outright, so the welcome message that
  // resetChatForNewProject would add never reappears.
  dispatch(setMessages(messages));

  // Having a conversation history means the use case was already chosen.
  dispatch(setUseCaseSelected(true));

  // Restore the use case from the project type so UI controls (e.g. file upload
  // being disabled for deal search) stay correct after rehydration.
  const projectType = selectCurrentProjectType(getState());
  if (projectType) {
    dispatch(setSelectedUseCase(projectType));
  }

  for (const [turnId, planData] of Object.entries(todoPlans)) {
    dispatch(
      setTurnTodoPlan({ turnId, plan: planData.plan, scopeSummary: planData.scopeSummary })
    );
  }

  restoreHistoricalOutputs(history.outputsByTurn, dispatch);
  restoreWorkspacePayload(history.latestWorkspacePayload, dispatch);
}

/** Show the progress bar for a generation that ran while the user was away. */
function applyGenerationStatus(
  processingStatus: string,
  projectId: string,
  dispatch: ChatDispatch
): void {
  if (IN_PROGRESS_STATUSES.includes(processingStatus)) {
    console.log("🔄 [REHYDRATE] Found in-progress generation, restoring progress bar");
    dispatch(addGenerationProgress({ projectId }));
    return;
  }

  if (processingStatus === "processed") {
    console.log("🔄 [REHYDRATE] Found completed generation, restoring progress bar at 100%");
    dispatch(addGenerationProgress({ projectId }));
    // Immediately update to processed state to show "Document ready!"
    dispatch(updateGenerationStatus({ projectId, status: "processed" }));
  }
}

/**
 * Phase 4: check for an in-progress generation. Only doc-drafter projects have
 * one, so deal search skips the call rather than collecting 404s.
 */
async function restoreGenerationProgress(
  projectId: string,
  signal: AbortSignal,
  dispatch: ChatDispatch
): Promise<void> {
  try {
    const { authenticatedFetch } = await import("../utils/authErrorHandler");
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
    const statusResponse = await authenticatedFetch(
      `${apiBaseUrl}/api/doc-drafter/status/${projectId}`,
      { signal }
    );

    // 404 means no generation started - that's fine, just skip
    if (!statusResponse.ok) return;

    const statusData = await statusResponse.json();
    applyGenerationStatus(statusData.processing_status, projectId, dispatch);
  } catch (statusError) {
    // Don't fail rehydration if the status check fails - it's optional
    if (!isAbortError(statusError)) {
      console.log("🔄 [REHYDRATE] Generation status check failed (non-critical):", statusError);
    }
  }
}

/** Nothing persisted for this project: keep the welcome message, show chat. */
function finishWithNoHistory(dispatch: ChatDispatch): void {
  dispatch(setLayoutMode("chat"));
  dispatch(setChatHistoryLoading(false));
}

/** Fetch the persisted turns. projectService is imported dynamically to avoid a cycle. */
async function fetchHistory(projectId: string, signal: AbortSignal): Promise<any> {
  const { getConversationHistory } = await import("../services/projectService");
  console.log("🔄 [REHYDRATE] Fetching conversation history from API");
  return getConversationHistory(projectId, signal);
}

/** Cancellations and 404s are routine; anything else is surfaced to the user. */
function reportRehydrationFailure(error: unknown, dispatch: ChatDispatch): void {
  // Aborts are expected when the user switches projects/chats
  if (isAbortError(error)) {
    console.log(
      "🛑 [REHYDRATE] Rehydration was cancelled (AbortError) - this is expected during project switch"
    );
    dispatch(setChatHistoryLoading(false));
    return;
  }

  const errorMessage =
    error instanceof Error ? error.message : "Failed to load conversation history";

  if (errorMessage.includes("404") || errorMessage.includes("not found")) {
    console.log("🔄 [REHYDRATE] Conversation not found (404) - showing fresh chat view");
    dispatch(setChatHistoryLoading(false));
    dispatch(setLayoutMode("chat"));
    return;
  }

  console.error("❌ [REHYDRATE] Failed to rehydrate conversation:", error);
  dispatch(setChatHistoryError(errorMessage));
  dispatch(setChatHistoryLoading(false));
  dispatch(setLayoutMode("chat"));
}

/**
 * Rehydrate conversation history for a project: fetch the historical turns from
 * the backend and rebuild the chat state.
 *
 * Expected API response schema (from conversation_history.py):
 * {
 *   conversationId: string,
 *   turns: [{ turnId, createdAt, userInputText, finalChatResponse, todoPlan,
 *             scopePlan, thumbsUp, thumbsDown, feedbackComment }],
 *   outputsByTurn: { [turnId]: [{ turnOutputId, outputType, content, createdAt }] },
 *   stateSnapshot: { dealRegistry, documentRegistry, facts },
 *   latestWorkspacePayload: { turnId, createdAt, results, documents, facts,
 *                             projectId, source } | null
 * }
 *
 * Phase 3: UI rehydration uses latestWorkspacePayload (the canonical snapshot)
 * instead of manually scanning outputsByTurn for results_payload entries.
 */
export const rehydrateConversation =
  (projectId: string) =>
  async (dispatch: any, getState: any): Promise<void> => {
    console.log("🔄 [REHYDRATE] rehydrateConversation called for projectId:", projectId);

    // Temporary projects have no conversation history yet
    if (projectId.startsWith(TEMP_PROJECT_PREFIX)) {
      console.log("🔄 [REHYDRATE] Temporary project detected - skipping API call");
      finishWithNoHistory(dispatch);
      return;
    }

    // Cancels any prior load
    const abortController = createConversationLoadAbortController();

    dispatch(setChatHistoryLoading(true));
    dispatch(setChatHistoryError(null));

    try {
      const history = await fetchHistory(projectId, abortController.signal);

      // The user may have switched to another project/chat while we waited
      if (abortController.signal.aborted) {
        console.log("🛑 [REHYDRATE] Request was cancelled - aborting rehydration");
        return;
      }
      console.log("🔄 [REHYDRATE] Received history:", history);

      if (!history.turns || history.turns.length === 0) {
        console.log("🔄 [REHYDRATE] No turns to rehydrate - keeping welcome message");
        finishWithNoHistory(dispatch);
        return;
      }

      applyRehydratedHistory(history, dispatch, getState);

      if (selectCurrentProjectType(getState()) === OFFER_LETTER_USE_CASE) {
        await restoreGenerationProgress(projectId, abortController.signal, dispatch);
      }

      console.log("✅ [REHYDRATE] Conversation rehydration complete");
      dispatch(setChatHistoryLoading(false));
    } catch (error) {
      reportRehydrationFailure(error, dispatch);
    }
  };
